import { CurrentTestState } from "../../storage"
import { FC, useCallback, useEffect, useState } from "react"
import { useSnapshot } from "valtio"
import {
  checkValidityString,
  getResponseSingleItem,
  useDebouncing,
  useReactiveItemResponse,
  useResponseUpdate,
  useStateReset
} from "../../helpers"
import { TextEditorComponent } from "../../ui/editors/TextEditor"
import { ERROR_MESSAGES } from "../../constants"
import {
  InteractionType,
  onSaveStudentAnswer,
  TextFormatType,
  isQuestionComplete,
  NeutralSetting
} from "../../types"

interface ExtendedTextInteractionProps {
  itemId: string
  responseidentifier: string
  expectedlength?: number
  expectedlengthdisplay?: "percentage" | "count"
  base?: number
  stringidentifier?: string
  patternmask?: string
  placeholdertext?: string
  format?: TextFormatType
  maxstrings?: number
  minstrings?: number
  expectedLines: number
  children?: JSX.Element
  accessibilityAttr?: { [key: string]: string }
  onSaveStudentAnswer?: onSaveStudentAnswer
  isCompletedQuestion?: isQuestionComplete
  isNeutralSetting?: NeutralSetting
}

interface ITextEditorValue {
  text: string
  xhtml: string
}

const getLinesCount = (response: string[]) =>
  response[0] ? response[0].split("\n").length - 1 : 0

const getFormatText = (value: ITextEditorValue, format: TextFormatType) => {
  switch (format) {
    case TextFormatType.plain:
      return value.text
    case TextFormatType.preformatted:
      return `<pre>${value.text}</pre>`
    case TextFormatType.xhtml:
      return value.xhtml
    default:
      return value.xhtml
  }
}

const xhtmlToText = (xhtml: string) =>
  xhtml
    .replace(/( |<([^>]+)>)/gi, "")
    .replace("\n", " ")
    .trim()

const transformResponse = (value: string) => [value]

export const ExtendedTextInteraction: FC<
  ExtendedTextInteractionProps
> = props => {
  const {
    responseidentifier: responseIdentifier,
    placeholdertext: placeholder,
    expectedlengthdisplay: expectedLengthDisplay,
    maxstrings: maxStrings = "0",
    minstrings: minStrings = "0",
    patternmask: patternMask,
    expectedlength: expectedLength,
    format,
    children,
    accessibilityAttr,
    onSaveStudentAnswer,
    isCompletedQuestion,
    isNeutralSetting
  } = props
  const { currentItemResponse, currentTestPart } = useSnapshot(CurrentTestState)
  const itemResponse = useReactiveItemResponse(responseIdentifier)

  const [text, setText] = useState("")
  const [xhtml, setXhtml] = useState("")
  const [lines, setLines] = useState<number>(null)
  const [isValid, setValid] = useState(true)
  const [error, setError] = useState<string>("")
  const debouncedValue = useDebouncing(getFormatText({ text, xhtml }, format))

  const { questionCompleted } = isCompletedQuestion?.() ?? {
    questionCompleted: false
  }

  useResponseUpdate(
    currentItemResponse?.id,
    responseIdentifier,
    debouncedValue?.trim() ?? "",
    InteractionType.extendedText,
    transformResponse,
    isValid
  )

  useEffect(() => {
    if (lines >= Number(minStrings) || lines <= Number(maxStrings)) {
      setValid(checkValidityString(patternMask, xhtmlToText(xhtml)))
    }
  }, [lines, xhtml, minStrings, maxStrings, patternMask])

  useEffect(() => {
    if (!itemResponse) return
    setText(xhtmlToText(getResponseSingleItem(itemResponse)))
    setXhtml(getResponseSingleItem(itemResponse))
    setLines(getLinesCount(itemResponse))
  }, [itemResponse])

  useStateReset(
    currentItemResponse?.id,
    responseIdentifier,
    setXhtml,
    getResponseSingleItem
  )
  useStateReset(
    currentItemResponse?.id,
    responseIdentifier,
    setLines,
    getLinesCount
  )

  const changeHandler = useCallback(
    (text: string, xhtml: string, countLines: number) => {
      setText(text)
      setXhtml(xhtml)
      setLines(countLines)
      // Clear error when user starts typing
      if (error) {
        setError("")
      }
    },
    [error]
  )

  // TODO: In the future, we should "catch" errors from the backend to display
  const onSubmit = useCallback(() => {
    // Validate that there's content before submitting
    const trimmedValue = debouncedValue?.trim()
    if (!trimmedValue) {
      setError(ERROR_MESSAGES.EMPTY_RESPONSE)
      return
    }

    onSaveStudentAnswer?.({
      interactionType: InteractionType.extendedText,
      answerId: debouncedValue,
      minChoices: 0,
      maxChoices: 1
    })
  }, [debouncedValue, onSaveStudentAnswer])

  // ExtendedText Accessibility Aria Attributes
  const editorAccessibilityAttr = {
    "aria-label": "Extended response text editor",
    "aria-multiline": "true",
    role: "textbox",
    ...accessibilityAttr
  }

  return (
    <>
      {children}
      {currentItemResponse.id && (
        <TextEditorComponent
          key={`${currentItemResponse.id}-${format}`}
          value={xhtml}
          {...editorAccessibilityAttr}
          changeHandler={changeHandler}
          placeholder={placeholder}
          isDisabled={currentTestPart.isFinished || questionCompleted}
          expectedLength={expectedLength}
          expectedLengthDisplay={expectedLengthDisplay}
          format={format}
          showSubmit={!!onSaveStudentAnswer}
          onSubmit={onSubmit}
          error={error}
        />
      )}
    </>
  )
}
